import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { MonthRef, shiftMonth, VISIBLE_MONTHS_COUNT } from 'modules/schedule/domain/utils/calendar-months';
import { setCurrentDate, setSelectedDate } from 'modules/schedule/store';
import { selectCurrentDate, selectSelectedDate } from 'modules/schedule/store/selectors';

/** Листание и выбор дня: состояние интерфейса календаря. Матчи — в `useMonthMatches`. */
export function useSchedule() {
  const dispatch = useAppDispatch();
  const currentDate = useAppSelector(selectCurrentDate);
  const selectedDate = useAppSelector(selectSelectedDate);

  /** Выбрать день календаря. */
  const pickDay = useCallback(
    (date: Date) => {
      dispatch(setSelectedDate(date.toISOString()));
    },
    [dispatch],
  );

  /** Показать месяц первым: переход стрелкой клавиатуры внутри календаря. */
  const showMonth = useCallback(
    (month: MonthRef) => {
      dispatch(setCurrentDate(month));
    },
    [dispatch],
  );

  /** Листать назад на столько месяцев, сколько показано. */
  const showPrevious = useCallback(() => {
    dispatch(setCurrentDate(shiftMonth(currentDate, -VISIBLE_MONTHS_COUNT)));
  }, [currentDate, dispatch]);

  /** Листать вперёд на столько месяцев, сколько показано. */
  const showNext = useCallback(() => {
    dispatch(setCurrentDate(shiftMonth(currentDate, VISIBLE_MONTHS_COUNT)));
  }, [currentDate, dispatch]);

  /** Вернуться к текущему месяцу и выбрать сегодняшний день. */
  const showToday = useCallback(() => {
    const today = new Date();
    dispatch(setCurrentDate({ month: today.getMonth(), year: today.getFullYear() }));
    dispatch(setSelectedDate(today.toISOString()));
  }, [dispatch]);

  return { currentDate, selectedDate, pickDay, showMonth, showPrevious, showNext, showToday };
}
